/**
 * Custom server emoji in the app (packages/protocol emoji.ts): how one is
 * drawn, and the text it travels as.
 *
 * - Everywhere the app holds "an emoji" as a string (the picker, recents,
 *   quick reactions, reaction pills), a custom one is its message form
 *   `<:name:id>` / `<a:name:id>`; `Emoji` (emoji.tsx) draws either kind.
 * - It is an <img> from our own server (`/api/v1/emojis/{id}.{ext}`, cached
 *   for good), lazily loaded; its alt is `:name:`, so copying gives the
 *   name. An emoji the person's servers don't have (deleted, or the
 *   console turned custom emoji off) is drawn as `:name:` text and never
 *   fetched.
 * - The composer shows `:name:` and sends `<:name:id>` (`encodeCustomEmoji`,
 *   never inside code); the edit box turns it back (`decodeCustomEmoji`).
 */
import {
  CUSTOM_EMOJI_SOURCE,
  customEmojiPath,
  formatCustomEmoji,
  parseCustomEmoji,
  type GuildEmoji,
} from '@neosocial/protocol';
import { useState } from 'react';
import { assetUrl } from '../lib/config';
import { customEmoji, customEmojiOn, useCustomEmojiOn, useEmoji } from '../store/emoji';

/** True for a custom emoji's message form. */
export function isCustomToken(s: string): boolean {
  return s.startsWith('<') && parseCustomEmoji(s) !== null;
}

/** The name a token shows (`:name:` without the colons); null for a deleted emoji's `_`. */
export function customName(token: string): string | null {
  const c = parseCustomEmoji(token);
  if (!c) return null;
  const known = customEmoji(c.id);
  if (known) return known.name;
  return c.name === '_' ? null : c.name;
}

/** True when the token names an emoji the app can draw now (known, and the console allows it). */
export function isDrawableCustom(token: string): boolean {
  const c = parseCustomEmoji(token);
  return !!c && customEmojiOn() && !!customEmoji(c.id);
}

/** The image URL of a custom emoji (our own origin). */
export function customEmojiUrl(e: Pick<GuildEmoji, 'id' | 'animated'>): string {
  return assetUrl(customEmojiPath(e.id, e.animated))!;
}

/** The token of a server emoji. */
export function tokenOf(e: Pick<GuildEmoji, 'id' | 'name' | 'animated'>): string {
  return formatCustomEmoji(e);
}

/** Draws a custom emoji token (see the file comment); `cls` as on `Emoji`. */
export function CustomEmoji({ token, cls }: { token: string; cls?: string }) {
  const c = parseCustomEmoji(token);
  const known = useEmoji((s) => (c ? s.byId[c.id] : undefined));
  const on = useCustomEmojiOn();
  const [broken, setBroken] = useState<string | null>(null);
  if (!c) return <span className={'nx-emoji is-text' + (cls ? ' ' + cls : '')}>{token}</span>;
  const name = known?.name ?? (c.name === '_' ? null : c.name);
  const label = name ? `:${name}:` : '?';
  if (!on || !known || broken === c.id)
    return (
      <span className={'nx-cemoji-text' + (cls ? ' ' + cls : '')} dir="ltr">
        {label}
      </span>
    );
  return (
    <img
      className={'nx-emoji is-custom' + (cls ? ' ' + cls : '')}
      src={customEmojiUrl(known)}
      alt={label}
      draggable={false}
      decoding="async"
      loading="lazy"
      onError={() => setBroken(c.id)}
    />
  );
}

const CODE = /```[\s\S]*?```|`[^`\n]+`/g;

/** Runs `fn` on the parts of `text` outside ``` blocks and `code`. */
function outsideCode(text: string, fn: (part: string) => string): string {
  let out = '';
  let last = 0;
  for (const m of text.matchAll(CODE)) {
    out += fn(text.slice(last, m.index)) + m[0];
    last = m.index + m[0].length;
  }
  return out + fn(text.slice(last));
}

/**
 * Typed `:name:` to `<:name:id>` for the emoji in `list` (the person's
 * servers'), as Discord's composer does; never inside code, never inside a
 * token already written out.
 */
export function encodeCustomEmoji(
  text: string,
  list: readonly Pick<GuildEmoji, 'id' | 'name' | 'animated'>[],
): string {
  if (!list.length || !text.includes(':')) return text;
  const byName = new Map(list.map((e) => [e.name, e]));
  return outsideCode(text, (part) =>
    part.replace(/(?<!<a?):([a-z0-9_]{2,32}):(?!\d+>)/g, (m, name: string) => {
      const e = byName.get(name);
      return e ? formatCustomEmoji(e) : m;
    }),
  );
}

/** The other way, for the edit box: known emoji back to `:name:` (unknown ones stay as they are). */
export function decodeCustomEmoji(content: string): string {
  if (!content.includes('<')) return content;
  return content.replace(new RegExp(CUSTOM_EMOJI_SOURCE, 'g'), (m, _a, _n, id: string) => {
    const e = customEmoji(id);
    return e ? `:${e.name}:` : m;
  });
}
